import classNames from 'classnames';
import type { ReactNode } from 'react';
import { renderToString } from 'react-dom/server';
import type { IControl, MapInstance } from 'react-map-gl/mapbox';

export interface ControlButton {
  // A string is set as text, anything else is rendered to static markup.
  content: ReactNode;
  title?: string;
  onClick?: () => void;
  disabled?: boolean;
  active?: boolean;
  isLabel?: boolean;
}

// A vertical mapbox control group whose buttons are plain DOM, re-rendered on every `render()`.
export abstract class ButtonGroupControl implements IControl {
  _container: HTMLElement | null = null;
  _map: MapInstance | undefined;

  constructor(private readonly className?: string) {}

  abstract getButtons(): Array<ControlButton>;

  render() {
    if (this._container == null) return;

    this._container.innerHTML = '';
    for (const { content, onClick, title, isLabel, disabled, active } of this.getButtons()) {
      const el = isLabel ? document.createElement('span') : document.createElement('button');

      if (isLabel) {
        el.className = 'mapbox-ctrl-mode-label';
      } else {
        el.className = 'mapbox-ctrl-button-style';
        if (active === true) el.classList.add('mapbox-ctrl-button-style-choosed');
        if (disabled === true) (el as HTMLButtonElement).disabled = true;
        if (onClick != null) el.addEventListener('click', onClick);
      }
      if (title != null) el.title = title;

      if (typeof content === 'string') el.textContent = content;
      else el.innerHTML = renderToString(content);
      this._container.appendChild(el);
    }
  }

  onAdd(map: MapInstance) {
    this._map = map;
    this._container = document.createElement('div');
    this._container.className = classNames(
      'mapboxgl-ctrl mapboxgl-ctrl-group mapboxgl-ctrl-group-vertical',
      this.className,
    );
    this._container.addEventListener('contextmenu', (e) => e.preventDefault());

    this.render();

    return this._container;
  }

  onRemove() {
    this._container?.parentNode?.removeChild(this._container);
    this._map = undefined;
  }
}
